import React, { useState, useEffect } from 'react';
import {
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, BarChart, Bar
} from 'recharts';
import {
    Phone, Users
} from 'lucide-react';
import apiService from '../../services/apiService';
import Card from '../../components/ui/card';
import MetricCard from '../../components/ui/metric-card';
import { useUiLanguage } from '../../context/UiLanguageContext';

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <div style={{ backgroundColor: 'var(--surface-raised)', border: '1px solid var(--border)', padding: '0.75rem', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-md)', color: 'var(--text)', zIndex: 50 }}>
                <p style={{ fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.875rem', color: 'var(--text)' }}>{label}</p>
                {payload.map((entry, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                        <div style={{ width: '0.5rem', height: '0.5rem', borderRadius: '50%', backgroundColor: entry.color }} />
                        <span style={{ textTransform: 'capitalize' }}>{entry.name}:</span>
                        <span style={{ fontWeight: 500, color: 'var(--text)' }}>{entry.value}</span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const VoiceAnalytics = ({ profileId = 'default', days = 7, showTest = false }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('volume');
    const { t, sklonit } = useUiLanguage();

    useEffect(() => {
        const fetchStats = async () => {
            setLoading(true);
            try {
                const data = showTest
                    ? await apiService.getVoiceAnalytics(days, profileId, true)
                    : await apiService.getVoiceAnalytics(days, profileId);
                setStats(data);
            } catch (error) {
                console.error("Failed to load voice stats", error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, [days, profileId, showTest]);

    if (loading) return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem 0', color: 'var(--text-muted)' }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border)', borderTop: '3px solid var(--accent)', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
    );

    if (!stats) return <div style={{ padding: '3rem 0', textAlign: 'center', color: 'var(--text-muted)' }}>{t('voice.analytics.load_error')}</div>;

    const COLORS = {
        POSITIVE: 'var(--success)',
        NEUTRAL: 'var(--text-muted)',
        NEGATIVE: 'var(--danger)',
        DEFAULT: 'var(--accent)'
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

            {/* Header */}
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                <div>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)', fontFamily: 'var(--font-display)', margin: 0 }}>
                        {t('voice.analytics.title')}
                    </h2>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginTop: '0.25rem', margin: 0 }}>
                        {t('voice.analytics.subtitle')}
                    </p>
                </div>
            </div>

            {/* Metrics Grid - 2 columns (Total Calls & Unique Callers) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <MetricCard
                    label={t('voice.analytics.total_calls')}
                    value={stats.overview.total_calls}
                    icon={<Phone size={18} />}
                    subtext={t('voice.analytics.sub_total_calls')}
                    variant="accent"
                />
                <MetricCard
                    label={t('voice.analytics.unique_callers')}
                    value={stats.overview.unique_callers}
                    icon={<Users size={18} />}
                    subtext={t('voice.analytics.sub_unique_callers')}
                    variant="accent"
                />
            </div>

            {/* Charts Area */}
            <Card style={{ backgroundColor: 'var(--surface-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }}>
                <div style={{ borderBottom: '1px solid var(--border)', padding: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                        <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text)', margin: 0 }}>
                            {t('voice.analytics.trends_title')}
                        </h3>
                        <div style={{ display: 'flex', backgroundColor: 'var(--surface-sunken)', padding: '0.25rem', borderRadius: 'var(--radius-md)' }}>
                            {[
                                { id: 'volume', labelKey: 'voice.analytics.tab_volume' },
                                { id: 'sentiment', labelKey: 'voice.analytics.tab_sentiment' },
                                { id: 'outcomes', labelKey: 'voice.analytics.tab_outcomes' },
                                { id: 'hourly', labelKey: 'voice.analytics.tab_hourly' }
                            ].map((tab) => (
                                <button
                                    key={tab.id}
                                    data-testid={`analytics-tab-${tab.id}`}
                                    onClick={() => setActiveTab(tab.id)}
                                    style={{
                                        padding: '0.375rem 0.75rem',
                                        borderRadius: 'var(--radius-sm)',
                                        fontSize: '0.875rem',
                                        fontWeight: 500,
                                        border: 'none',
                                        cursor: 'pointer',
                                        backgroundColor: activeTab === tab.id ? 'var(--surface-raised)' : 'transparent',
                                        color: activeTab === tab.id ? 'var(--text)' : 'var(--text-muted)',
                                        boxShadow: activeTab === tab.id ? 'var(--shadow-sm)' : 'none',
                                        transition: 'all 0.15s ease'
                                    }}
                                >
                                    {t(tab.labelKey)}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div style={{ padding: '1.5rem' }}>
                    {/* VOLUME CHART */}
                    {activeTab === 'volume' && (
                        <div style={{ height: '350px', width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={stats.volume} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="var(--accent)" stopOpacity={0.2} />
                                            <stop offset="95%" stopColor="var(--accent)" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                                    <XAxis
                                        dataKey="date"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: 'var(--text-muted)', fontSize: 12 }}
                                        dy={10}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: 'var(--text-muted)', fontSize: 12 }}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Area
                                        type="monotone"
                                        dataKey="count"
                                        stroke="var(--accent)"
                                        strokeWidth={2}
                                        fill="url(#colorCount)"
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* SENTIMENT CHART */}
                    {activeTab === 'sentiment' && (
                        <div style={{ height: '350px', width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={stats.sentiment}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={80}
                                        outerRadius={110}
                                        paddingAngle={2}
                                        dataKey="value"
                                    >
                                        {stats.sentiment.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[entry.name] || COLORS.DEFAULT} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* OUTCOMES CHART */}
                    {activeTab === 'outcomes' && (
                        <div style={{ height: '350px', width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={stats.outcomes} layout="vertical" margin={{ left: 40, right: 40 }}>
                                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border)" />
                                    <XAxis type="number" hide />
                                    <YAxis
                                        dataKey="name"
                                        type="category"
                                        axisLine={false}
                                        tickLine={false}
                                        width={100}
                                        tick={{ fill: 'var(--text-muted)', fontSize: 12, fontWeight: 500 }}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={32}>
                                        {stats.outcomes.map((entry, index) => (
                                             <Cell key={`cell-${index}`} fill="var(--accent)" />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* HOURLY CHART */}
                    {activeTab === 'hourly' && (
                        <div style={{ height: '350px', width: '100%' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={stats.hourly}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
                                    <XAxis
                                        dataKey="hour"
                                        axisLine={false}
                                        tickLine={false}
                                        tickFormatter={(val) => `${val}:00`}
                                        tick={{ fill: 'var(--text-muted)', fontSize: 12 }}
                                        dy={10}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: 'var(--text-muted)', fontSize: 12 }}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Bar dataKey="count" fill="var(--accent)" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default VoiceAnalytics;
